import { AUTH_INPUT } from '@/components/auth-layout'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { EMAIL_CHANGE_OTP_LENGTH, useEmailChange } from '@/domains/auth'
import { cn } from '@/lib/utils'

const PRIMARY_BUTTON =
  'w-full cursor-pointer rounded-xl bg-accent py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent'

const LINK_BUTTON =
  'inline-flex min-h-11 cursor-pointer items-center justify-center px-3 py-2 text-sm font-medium text-accent transition-colors hover:text-[#15296b] disabled:cursor-not-allowed disabled:text-ink-muted sm:min-h-0 dark:text-accent-mid'

export function WorkEmailSwitch() {
  const {
    state,
    dispatch,
    currentEmail,
    codeAddress,
    resendCooldown,
    start,
    confirmCode,
    resend,
    reset
  } = useEmailChange()
  const stepNumber =
    state.step === 'email' ? 1 : state.step === 'current-code' ? 2 : 3

  function handleCodeChange(value: string) {
    dispatch({ type: 'SET_CODE', payload: value })
    if (value.length === EMAIL_CHANGE_OTP_LENGTH && !state.submitting) {
      void confirmCode(value)
    }
  }

  return (
    <div className='mx-auto flex w-full max-w-[440px] flex-col py-2 sm:py-10'>
      <div className='flex items-center gap-3'>
        <div className='flex flex-1 gap-1.5'>
          {[1, 2, 3].map(n => (
            <span
              key={n}
              className={cn(
                'h-1 flex-1 rounded-full',
                n <= stepNumber ? 'bg-accent' : 'bg-border'
              )}
            />
          ))}
        </div>
        <span className='shrink-0 text-[12px] font-medium text-ink-muted'>
          Step {stepNumber} of 3
        </span>
      </div>

      {state.step === 'email' ? (
        <>
          <h1 className='mt-6 font-display text-[28px] leading-[1.15] tracking-[-0.5px] text-ink sm:text-[32px]'>
            Switch to your work email
          </h1>
          <p className='mt-3 text-[15px] leading-relaxed text-ink-soft'>
            You signed up with{' '}
            <strong className='break-all text-ink'>{currentEmail}</strong>. We
            approve buyer accounts that use a company email address.
          </p>

          <form
            onSubmit={event => {
              event.preventDefault()
              void start()
            }}
            className='mt-8 flex flex-col gap-4'
          >
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='work-email'>Work email</Label>
              <Input
                id='work-email'
                type='email'
                autoComplete='email'
                placeholder='you@yourcompany.com'
                value={state.newEmail}
                onChange={event =>
                  dispatch({
                    type: 'SET_NEW_EMAIL',
                    payload: event.target.value
                  })
                }
                className={AUTH_INPUT}
                autoFocus
              />
            </div>
            {state.error && (
              <p className='text-sm text-red-600'>{state.error}</p>
            )}
            <button
              type='submit'
              disabled={state.newEmail.trim().length === 0 || state.submitting}
              className={PRIMARY_BUTTON}
            >
              {state.submitting ? 'Sending...' : 'Continue'}
            </button>
          </form>
          <p className='mt-4 text-center text-[13px] text-ink-muted'>
            We’ll send a code to each address to confirm the change.
          </p>
        </>
      ) : (
        <>
          <h1 className='mt-6 font-display text-[28px] leading-[1.15] tracking-[-0.5px] text-ink sm:text-[32px]'>
            {state.step === 'current-code'
              ? 'Confirm it’s you'
              : 'Check your work email'}
          </h1>
          <p className='mt-3 text-[15px] leading-relaxed text-ink-soft'>
            We sent a 6-digit code to{' '}
            <strong className='break-all text-ink'>{codeAddress}</strong>.
          </p>

          <div className='mt-8 flex flex-col gap-5'>
            <div className='flex justify-center'>
              <InputOTP
                key={state.step}
                aria-label='Code'
                maxLength={EMAIL_CHANGE_OTP_LENGTH}
                value={state.code}
                onChange={handleCodeChange}
                disabled={state.submitting}
                autoFocus
              >
                <InputOTPGroup>
                  {Array.from({ length: EMAIL_CHANGE_OTP_LENGTH }, (_, i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {state.error && (
              <p className='text-center text-sm text-red-600'>{state.error}</p>
            )}
            <button
              type='button'
              onClick={() => void confirmCode(state.code)}
              disabled={
                state.code.length !== EMAIL_CHANGE_OTP_LENGTH ||
                state.submitting
              }
              className={PRIMARY_BUTTON}
            >
              {state.submitting ? 'Verifying...' : 'Verify'}
            </button>
            <div className='flex flex-col items-center text-sm text-ink-soft sm:flex-row sm:justify-center sm:gap-2'>
              <span>Didn’t get it?</span>
              <button
                type='button'
                onClick={() => void resend()}
                disabled={resendCooldown > 0 || state.submitting}
                className={LINK_BUTTON}
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend code'}
              </button>
              <span aria-hidden className='hidden sm:inline'>
                ·
              </span>
              <button type='button' onClick={reset} className={LINK_BUTTON}>
                Use a different email
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
